import React from 'react';
import { motion } from 'motion/react';
import { Shield, FileText, Lock, Eye, Globe, Scale } from 'lucide-react';

const Section = ({ title, icon: Icon, children }: { title: string, icon: any, children: React.ReactNode }) => (
  <section className="mb-12">
    <div className="flex items-center gap-3 mb-4 text-accent">
      <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </div>
      <h2 className="text-xl font-bold dark:text-white">{title}</h2>
    </div>
    <div className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm space-y-4">
      {children}
    </div>
  </section>
);

export default function PrivacyPolicy() {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 md:p-20 max-w-4xl mx-auto"
    >
      <header className="mb-16">
        <span className="text-[10px] font-black uppercase tracking-[0.3em] text-accent mb-4 block">Compliance & Trust</span>
        <h1 className="text-5xl font-black tracking-tight dark:text-white mb-6">Privacy Policy</h1>
        <p className="text-slate-500 dark:text-slate-400 text-lg max-w-2xl">
          At QuicklyUtils, we prioritize your data security and transparency. This policy outlines how we handle information and ensure your digital safety.
        </p>
        <div className="mt-8 pt-8 border-t border-line dark:border-slate-800 flex gap-8 text-[10px] font-bold uppercase tracking-widest text-slate-400">
          <div>Released: April 2026</div>
          <div>Version: 1.0.0</div>
        </div>
      </header>

      <Section title="Information Collection" icon={Eye}>
        <p>
          We collect minimal data necessary to provide our services. This includes authentication data (via Google Log-in) if you choose to sync your favorites across devices.
        </p>
        <p>
          For anonymous users, we store settings like Dark Mode and Favorites locally on your browser (Local Storage).
        </p>
      </Section>

      <Section title="Google Advertising" icon={Globe}>
        <p>
          QuicklyUtils may use Google AdSense to serve advertisements. Google uses cookies to serve ads based on a user's prior visits to our website or other websites.
        </p>
        <p>
          Google's use of advertising cookies enables it and its partners to serve ads to users based on their visit to your sites and/or other sites on the Internet.
        </p>
        <p>
          Users may opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" className="text-accent underline" target="_blank" rel="noreferrer">Ads Settings</a>.
        </p>
      </Section>

      <Section title="Data Usage" icon={FileText}>
        <p>
          Your data is used solely to personalize your experience. We do not sell your personal information to third parties. 
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Favorites: Used to pin tools you use frequently.</li>
          <li>History: Used to let you quickly return to recent tools.</li>
          <li>Suggestions: Used to improve and expand our tool library.</li>
          <li>Service Management: Platform administrators may access basic account info (name, email, login time) to maintain service integrity and provide support.</li>
          <li>Newsletter: If you subscribe, we store your email to send updates. You can unsubscribe at any time via the link in the email.</li>
        </ul>
      </Section>

      <Section title="Data Security" icon={Lock}>
        <p>
          We employ industry-standard encryption and security protocols provided by Firebase (Google Cloud) to protect your authenticated data.
        </p>
        <p>
          While we strive to use commercially acceptable means to protect your Personal Information, we cannot guarantee its absolute security as no method of transmission over the Internet is 100% secure.
        </p>
      </Section>

      <Section title="User Rights" icon={Scale}>
        <p>
          You have the right to access, update, or delete your information at any time through our application settings. Clearing your browser cache will also remove any local unauthenticated data.
        </p>
      </Section>

      <footer className="mt-20 pt-8 border-t border-line dark:border-slate-800 text-center">
        <p className="text-slate-400 text-xs italic">
          If you have questions about this privacy policy, please reach out via our feedback system.
        </p>
      </footer>
    </motion.div>
  );
}
